import { stageKeyOf } from './stageKeys';
import type { LeadStage } from './types';

/**
 * Converted to Deal is reached by raising a deal, not by picking it.
 *
 * ── The measurement that caused this ───────────────────────────────────────
 *
 * Production, 2026-08-27: of nine leads at Converted to Deal, three had a
 * linked deal and six did not. Won and Lost are read off the deals rather than
 * stored, so a converted lead with no deal has nothing to read — it can never
 * reach either, and reports as a live opportunity forever. Two thirds of the
 * bucket was in that state.
 *
 * The cause is that the status dropdown offered the value. `promote_lead_to_deal`
 * exists, raises the deal, moves the status and carries the company across —
 * and nobody knew the button was there.
 *
 * ── Why this is a UI rule and not a database trigger ───────────────────────
 *
 * A database guard was written first and backed out. It refused the move twice
 * in the migration harness, both times inside `remap_legacy_lead_statuses`,
 * which moves legacy Active Deal and Won leads onto converted precisely
 * because they predate deals existing. Scoping the trigger to signed-in
 * writes did not help either: the harness runs that remap with a user set.
 *
 * The rule that is actually wanted is "the dropdown may not offer this". The
 * database cannot tell a dropdown from a migration replaying our own history,
 * so expressing it there meant guessing at the difference. It belongs where
 * the cause is.
 *
 * The trade is stated plainly: a direct API or SQL write can still create the
 * stuck state. Nothing in the six measured leads got there that way.
 */

/** Options the status select may offer for a lead currently on `currentStageId`. */
export function selectableStages(stages: LeadStage[], currentStageId: string): LeadStage[] {
  return stages.filter((stage) => {
    if (stageKeyOf(stage) !== 'converted') {
      return true;
    }
    // A lead already sitting there keeps its own value in the list, or the
    // select would render blank and the first change would move it silently.
    return stage.id === currentStageId;
  });
}

/** True when the select is hiding Converted to Deal, so the hint can say why. */
export function convertedHidden(stages: LeadStage[], currentStageId: string): boolean {
  return selectableStages(stages, currentStageId).length < stages.length;
}

export const CONVERTED_HINT =
  'Converted to Deal is set by raising the deal — use Promote to Deal below, which creates it, links it and moves the status.';
